/**
 * Countries where IRS EINs apply: the 50 states, DC, and U.S. territories.
 * Freely associated states (Palau, Marshall Islands, Micronesia) are not
 * included.
 */
const US_EIN_COUNTRIES = new Set(
  [
    'US',
    'USA',
    'United States',
    'United States of America',
    'PR',
    'Puerto Rico',
    'GU',
    'Guam',
    'AS',
    'American Samoa',
    'MP',
    'Northern Mariana Islands',
    'Commonwealth of the Northern Mariana Islands',
    'CNMI',
    'VI',
    'USVI',
    'Virgin Islands (US)',
    'Virgin Islands, U.S.',
    'U.S. Virgin Islands',
    'United States Virgin Islands',
    'US Virgin Islands',
  ].map(normalizeEinCountry)
);

function normalizeEinCountry(country) {
  return String(country || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function isUsEinCountry(country) {
  return US_EIN_COUNTRIES.has(normalizeEinCountry(country));
}

/**
 * Validates EIN format and detects obviously fake numbers.
 * USA EIN format: XX-XXXXXXX (9 digits with hyphen after 2nd digit)
 *
 * @param {string|number} ein - The EIN to validate (JSON may send a number)
 * @param {string} [country='US'] - Country code (US, CA, etc.)
 * @returns {{ valid: boolean, reason?: string, formatted?: string }}
 */
function validateEIN(ein, country = 'US') {
  if (ein == null || ein === '') return { valid: false, reason: 'empty' };

  // JSON bodies can send EIN as a number. Reject other types so String(ein)
  // cannot turn objects/booleans into a "valid" non-US business number.
  if (typeof ein === 'number') {
    if (!Number.isFinite(ein)) {
      return { valid: false, reason: 'format' };
    }
  } else if (typeof ein !== 'string') {
    return { valid: false, reason: 'format' };
  }

  const einString = String(ein).trim();
  if (!einString) return { valid: false, reason: 'empty' };

  const cleanEIN = einString.replace(/[-\s]/g, '');

  const isUS = isUsEinCountry(country);

  if (isUS) {
    // Must be 9 digits
    if (!/^\d{9}$/.test(cleanEIN)) {
      return { valid: false, reason: 'format' };
    }

    // Detect obviously fake patterns
    const fakePatterns = [
      /^0{9}$/, // 00-0000000
      /^1{9}$/, // 11-1111111
      /^(.)\1{8}$/, // Any repeating digit
      /^123456789$/, // Sequential
      /^987654321$/, // Reverse sequential
      /^0{2}/, // Starts with 00 (invalid prefix)
      /^07/, // 07 prefix not assigned
      /^08/, // 08 prefix not assigned
      /^09/, // 09 prefix not assigned
      /^17/, // 17 prefix not assigned
      /^18/, // 18 prefix not assigned
      /^19/, // 19 prefix not assigned
      /^28/, // 28 prefix not assigned
      /^29/, // 29 prefix not assigned
      /^49/, // 49 prefix not assigned
      /^69/, // 69 prefix not assigned
      /^70/, // 70 prefix not assigned
      /^78/, // 78 prefix not assigned
      /^79/, // 79 prefix not assigned
      /^89/, // 89 prefix not assigned
    ];

    for (const pattern of fakePatterns) {
      if (pattern.test(cleanEIN)) {
        return { valid: false, reason: 'invalid_pattern' };
      }
    }

    return {
      valid: true,
      formatted: `${cleanEIN.slice(0, 2)}-${cleanEIN.slice(2)}`,
    };
  }

  // Non-US: just check it's not empty and reasonable length
  if (cleanEIN.length < 5 || cleanEIN.length > 20) {
    return { valid: false, reason: 'length' };
  }

  return { valid: true, formatted: einString };
}

function getEINValidationMessage(reason, country = 'US') {
  const isUS = isUsEinCountry(country);

  if (isUS) {
    switch (reason) {
      case 'empty':
        return 'Enter an EIN.';
      case 'format':
        return 'Enter a valid EIN in the format XX-XXXXXXX.';
      case 'invalid_pattern':
        return 'Enter a real EIN. Repeated digits, placeholder numbers, and unassigned prefixes are not allowed.';
      default:
        return 'Enter a valid EIN.';
    }
  }

  switch (reason) {
    case 'empty':
      return 'Enter a business number.';
    case 'length':
      return 'Enter a valid business number between 5 and 20 characters.';
    default:
      return 'Enter a valid business number.';
  }
}

module.exports = {
  validateEIN,
  getEINValidationMessage,
  isUsEinCountry,
};
